/**
 * SearchArtifacts slice — generic, filter-driven artifact search.
 *
 * searchArtifacts(input): POST /artifacts/search via beaconApi; stores the raw
 * SearchArtifacts summaries + total count. Callers supply the
 * SearchArtifactsInput (filters/pagination/sort). The bulk artifact search page
 * uses it with filters.artifactIds = [queried ids].
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiArtifactSummary, SearchArtifactsInput } from './searchArtifactsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export interface SearchArtifactsState {
  artifacts: ApiArtifactSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
}

export const searchArtifactsInitialState: SearchArtifactsState = {
  artifacts: [],
  total: 0,
  loading: false,
  error: null,
  currentRequestId: null,
};

interface FetchResult {
  artifacts: ApiArtifactSummary[];
  total: number;
}

export const searchArtifacts = createAsyncThunk<
  FetchResult, SearchArtifactsInput, { state: RootState; rejectValue: string }
>('searchArtifacts/run', async (input, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const response = await beaconApi.searchArtifacts(input, apiBase);
    return { artifacts: response.artifacts ?? [], total: response.totalArtifactsCount ?? 0 };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to search artifacts');
  }
});

const searchArtifactsSlice = createSlice({
  name: 'searchArtifacts',
  initialState: searchArtifactsInitialState,
  reducers: {
    clearSearchArtifacts: () => searchArtifactsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchArtifacts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(searchArtifacts.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.artifacts = action.payload.artifacts;
        state.total = action.payload.total;
      })
      .addCase(searchArtifacts.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to search artifacts';
      });
  },
});

export const { clearSearchArtifacts } = searchArtifactsSlice.actions;
export default searchArtifactsSlice.reducer;
